import React from "react";

import TextBox from "../../../Common/TextBox";
import MtiButton from "../../../Buttons/MtiButton";
import HotelCalibrationTable, {
  HotelOffsetTableColumn,
} from "../../../Common/HotelOffsetTable";

import { EFootprint } from "../../../../Model/PlateType";
import {
  Ri360HotelCalibration,
  Ri360CarrierCalibration,
} from "../../../../Model/StorageConfig";

import styles from "./HotelOffset.module.css";

export interface HotelTableRow {
  id: string;
  number: number;
  selected: boolean;
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  offsetT: number;
}

type HotelOffsetProps = {
  storageCalibration: Ri360CarrierCalibration;
  setStorageCalibration: (storageCalibration: Ri360CarrierCalibration) => void;
  selectedHotelNumber: number;
  setSelectedHotelNumber: (selectedHotelNumber: number) => void;
  canSet: boolean;
  onSet: () => void;
  onClearAll: () => void;
  onClearSelectedIndex: () => void;
  onReset: () => void;
};

export default function HotelOffset({
  storageCalibration,
  setStorageCalibration,
  selectedHotelNumber,
  setSelectedHotelNumber,
  canSet,
  onSet,
  onClearAll,
  onClearSelectedIndex,
  onReset,
}: HotelOffsetProps) {
  const data: HotelTableRow[] = storageCalibration.hotelCalibrations.map(
    (hotel) => {
      return {
        id: `Hotel ${EFootprint[hotel.footprint]} ${hotel.number}`,
        number: hotel.number,
        offsetX: hotel.offsetX,
        offsetY: hotel.offsetY,
        offsetZ: hotel.offsetZ,
        offsetT: 0,
        selected: hotel.number === selectedHotelNumber,
      };
    }
  );

  const onRowSelected = (selectedRow: HotelTableRow) => {
    setSelectedHotelNumber(selectedRow.number);
  };

  const onRowUpdated = (selectedRow: HotelTableRow) => {
    let updatedHotels = [...storageCalibration.hotelCalibrations];
    const updatedIndex = updatedHotels.findIndex(
      (hotel) => hotel.number === selectedRow.number
    );

    const newSelectedHotel: Ri360HotelCalibration = {
      ...storageCalibration.hotelCalibrations[updatedIndex],
      offsetX: selectedRow.offsetX,
      offsetY: selectedRow.offsetY,
      offsetZ: selectedRow.offsetZ,
    };

    updatedHotels[updatedIndex] = newSelectedHotel;
    setStorageCalibration({
      ...storageCalibration,
      hotelCalibrations: updatedHotels,
    });
  };

  const tableColumnCartesian: HotelOffsetTableColumn[] = [
    {
      id: "id",
      header: "Hotel#",
      accessor: (row) => {
        return row.id;
      },
      width: "20%",
    },
    {
      id: "offsetX",
      header: "X",
      accessor: (row) => {
        return (
          <TextBox
            name="offsetX"
            variant="number"
            isInteger={false}
            value={+row.offsetX.toFixed(2)}
            readOnly={!row.selected}
            onChange={(value) =>
              onRowUpdated({
                ...row,
                offsetX: value as number,
              })
            }
          />
        );
      },
      width: "15%",
    },
    {
      id: "offsetY",
      header: "Y",
      accessor: (row) => {
        return (
          <TextBox
            name="offsetY"
            variant="number"
            isInteger={false}
            value={+row.offsetY.toFixed(2)}
            readOnly={!row.selected}
            onChange={(value) =>
              onRowUpdated({
                ...row,
                offsetY: value as number,
              })
            }
          />
        );
      },
      width: "15%",
    },
    {
      id: "offsetZ",
      header: "Z",
      accessor: (row) => {
        return (
          <TextBox
            name="offsetZ"
            variant="number"
            isInteger={false}
            value={+row.offsetZ.toFixed(2)}
            readOnly={!row.selected}
            onChange={(value) =>
              onRowUpdated({
                ...row,
                offsetZ: value as number,
              })
            }
          />
        );
      },
      width: "15%",
    },
    {
      id: "action",
      header: "Action",
      accessor: (row) => {
        return (
          <div style={{ display: "flex", gap: 8 }}>
            <MtiButton
              colorVariant="black"
              size="medium"
              onClick={onSet}
              disabled={!row.selected || !canSet}
            >
              SET OFFSET
            </MtiButton>
            <MtiButton
              colorVariant="black"
              size="medium"
              onClick={onClearSelectedIndex}
              disabled={!row.selected}
            >
              CLEAR
            </MtiButton>
          </div>
        );
      },
      width: "35%",
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.title}>Hotel Offset</div>
      <div className={styles.table}>
        <HotelCalibrationTable
          data={data}
          columns={tableColumnCartesian}
          onRowSelected={onRowSelected}
        />
      </div>
      <div className={styles.button}>
        <MtiButton
          colorVariant="black"
          variant="secondary"
          size="medium"
          onClick={onClearAll}
        >
          CLEAR ALL
        </MtiButton>
        <MtiButton
          colorVariant="black"
          variant="secondary"
          size="medium"
          onClick={onReset}
        >
          RESET
        </MtiButton>
      </div>
    </div>
  );
}
